import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Grid,
  Alert,
  Skeleton,
  Switch,
  FormControlLabel,
  Badge,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  VisibilityOff as ViewOffIcon,
  Download as DownloadIcon,
  Psychology as PsychologyIcon,
  Work as WorkIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as DebugIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  SmartToy as AgentIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { logsAPI } from '../services/api';

// Log Entry Component
const LogEntry = ({ log, showDetails = false }) => {
  const getLevelIcon = (level) => {
    switch (level) {
      case 'ERROR': return <ErrorIcon />;
      case 'WARNING': return <WarningIcon />;
      case 'INFO': return <InfoIcon />;
      case 'DEBUG': return <DebugIcon />;
      default: return <InfoIcon />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'error';
      case 'WARNING': return 'warning';
      case 'INFO': return 'info';
      case 'DEBUG': return 'default';
      default: return 'default';
    }
  };

  const getServiceIcon = (service) => {
    switch (service) {
      case 'agent': return <AgentIcon />;
      case 'harvest': return <SearchIcon />;
      case 'candidates': return <WorkIcon />;
      case 'job_batches': return <WorkIcon />;
      case 'browser': return <ViewIcon />;
      case 'api': return <SettingsIcon />;
      default: return <InfoIcon />;
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const isAgentLog = log.type === 'agent_thought' || log.type === 'agent_action' || log.type === 'agent_result';

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
    >
      <Card 
        sx={{ 
          mb: 1, 
          borderLeft: `4px solid`,
          borderLeftColor: `${getLevelColor(log.level)}.main`,
          backgroundColor: isAgentLog ? 'primary.50' : 'background.paper',
          '&:hover': {
            backgroundColor: isAgentLog ? 'primary.100' : 'action.hover',
          }
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Level Icon */}
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: `${getLevelColor(log.level)}.main`,
                mt: 0.5
              }}
            >
              {getLevelIcon(log.level)}
            </Avatar>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {log.message}
                </Typography>
                <Chip 
                  label={log.level} 
                  size="small" 
                  color={getLevelColor(log.level)}
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
                <Chip 
                  label={log.service} 
                  size="small" 
                  variant="outlined"
                  icon={getServiceIcon(log.service)}
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              </Box>

              {/* Timestamp and Details */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatTimestamp(log.timestamp)}
                </Typography>
                {log.request_id && (
                  <Typography variant="caption" color="text.secondary">
                    Request: {log.request_id.slice(0, 8)}...
                  </Typography>
                )}
                {log.user_id && (
                  <Typography variant="caption" color="text.secondary">
                    User: {log.user_id}
                  </Typography>
                )}
              </Box>

              {/* Agent-specific details */}
              {isAgentLog && showDetails && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                  {log.type === 'agent_thought' && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Context:</strong> {JSON.stringify(log.context, null, 2)}
                    </Typography>
                  )}
                  {log.type === 'agent_action' && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Details:</strong> {JSON.stringify(log.details, null, 2)}
                    </Typography>
                  )}
                  {log.type === 'agent_result' && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Success:</strong> {log.success ? 'Yes' : 'No'}
                      {log.details && (
                        <Box sx={{ mt: 0.5 }}>
                          <strong>Details:</strong> {JSON.stringify(log.details, null, 2)}
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Log Stats Component
const LogStats = ({ stats }) => {
  if (!stats) return null;

  const statItems = [
    { label: 'Total Logs', value: stats.total_logs, color: 'primary' },
    { label: 'Errors', value: stats.errors, color: 'error' },
    { label: 'Warnings', value: stats.warnings, color: 'warning' },
    { label: 'Info', value: stats.info, color: 'info' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((item) => (
        <Grid item xs={6} sm={3} key={item.label}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: `${item.color}.main` }}>
                {item.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const AgentConsole = () => {
  const [filters, setFilters] = useState({
    service: '',
    level: '',
    user_id: '',
  });
  const [logs, setLogs] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const logsEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  // Queries
  const { data: services, isLoading: servicesLoading } = useQuery(
    'logServices',
    () => logsAPI.getServices(),
    { staleTime: 300000 } // 5 minutes
  );

  const { data: stats, isLoading: statsLoading } = useQuery(
    ['logStats', filters],
    () => logsAPI.getStats({ ...filters, hours: 24 }),
    { 
      refetchInterval: 30000, // 30 seconds
      staleTime: 60000 
    }
  );

  // Load initial logs
  useEffect(() => {
    loadLogs();
  }, [filters]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const loadLogs = async () => {
    try {
      const response = await logsAPI.getLogs({ ...filters, limit: 100 });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const startStreaming = () => {
    if (isStreaming) return;

    setIsStreaming(true);
    eventSourceRef.current = logsAPI.streamLogs(
      filters,
      (data) => {
        if (data.type === 'log_entry') {
          setLogs(prev => [data.data, ...prev.slice(0, 999)]); // Keep max 1000 logs
        }
      },
      (error) => {
        console.error('Stream error:', error);
        setIsStreaming(false);
      },
      () => {
        setIsStreaming(false);
      }
    );
  };

  const stopStreaming = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const exportLogs = () => {
    const logText = logs.map(log => 
      `${log.timestamp} [${log.level}] [${log.service}] ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Agent Console
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor your LinkedIn Agent's real-time activities and thought process
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={isStreaming ? "outlined" : "contained"}
            startIcon={isStreaming ? <StopIcon /> : <PlayIcon />}
            onClick={isStreaming ? stopStreaming : startStreaming}
            color={isStreaming ? "error" : "primary"}
          >
            {isStreaming ? 'Stop Streaming' : 'Start Streaming'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportLogs}
            disabled={logs.length === 0}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      {!statsLoading && stats?.data?.stats && (
        <LogStats stats={stats.data.stats} />
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Live Logs" />
          <Tab label="Filters & Settings" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          {/* Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                />
              }
              label="Auto-scroll"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showDetails}
                  onChange={(e) => setShowDetails(e.target.checked)}
                />
              }
              label="Show Details"
            />
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadLogs}
            >
              Refresh
            </Button>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearLogs}
              disabled={logs.length === 0}
            >
              Clear
            </Button>
          </Box>

          {/* Logs Display */}
          <Card sx={{ height: '60vh', overflow: 'hidden' }}>
            <CardContent sx={{ height: '100%', p: 0 }}>
              <Box sx={{ 
                height: '100%', 
                overflow: 'auto', 
                p: 2,
                backgroundColor: 'background.default'
              }}>
                {logs.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    color: 'text.secondary'
                  }}>
                    <AgentIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No logs yet
                    </Typography>
                    <Typography variant="body2">
                      {isStreaming 
                        ? 'Waiting for agent activity...' 
                        : 'Start streaming to see agent logs'
                      }
                    </Typography>
                  </Box>
                ) : (
                  <AnimatePresence>
                    {logs.map((log, index) => (
                      <LogEntry 
                        key={`${log.timestamp}-${index}`} 
                        log={log} 
                        showDetails={showDetails}
                      />
                    ))}
                  </AnimatePresence>
                )}
                <div ref={logsEndRef} />
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Filters & Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Service</InputLabel>
                  <Select
                    value={filters.service}
                    onChange={(e) => handleFilterChange('service', e.target.value)}
                  >
                    <MenuItem value="">All Services</MenuItem>
                    {services?.data?.services?.map((service) => (
                      <MenuItem key={service.name} value={service.name}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Log Level</InputLabel>
                  <Select
                    value={filters.level}
                    onChange={(e) => handleFilterChange('level', e.target.value)}
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    <MenuItem value="ERROR">Error</MenuItem>
                    <MenuItem value="WARNING">Warning</MenuItem>
                    <MenuItem value="INFO">Info</MenuItem>
                    <MenuItem value="DEBUG">Debug</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  placeholder="Filter by user ID"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AgentConsole; 